import React, { useEffect, useState } from "react";
import { Stack, MenuItem, Typography, useTheme, Tooltip, Box, CircularProgress, InputAdornment } from "@mui/material";
import { useFormContext } from "react-hook-form";
// @ts-ignore
import { State, City } from "country-state-city";

import { RHFSelect } from "@app/components/hook-form";
import { RHFTextField } from "@app/components/hook-form/RHFTextField";
import { useResponsive } from "@app/hooks";

interface LocationSelectorProps {
  layout?: "row" | "column";
  variant?: "default" | "grey" | "mobileInfo" | "userInfo";
  namePrefix?: string;
}

const LocationSelector = ({ layout, variant = "grey", namePrefix = "" }: LocationSelectorProps) => {
  const isMobile = useResponsive("down", "md");
  const theme = useTheme();
  const { watch, setValue } = useFormContext();

  const stateField = namePrefix ? `${namePrefix}.state` : "state";
  const cityField = namePrefix ? `${namePrefix}.city` : "city";
  const zipCodeField = namePrefix ? `${namePrefix}.zipCode` : "zipCode";

  const selectedState = watch(stateField);
  const selectedCity = watch(cityField);
  const [cities, setCities] = useState<any[]>([]);
  const [loadingPincode, setLoadingPincode] = useState(false);

  const states = State.getStatesOfCountry("IN");

  useEffect(() => {
    if (selectedState) {
      const stateObj = states.find((s: any) => s.name === selectedState);
      if (stateObj) {
        setCities(City.getCitiesOfState("IN", stateObj.isoCode));
      } else {
        setCities([]);
      }
    } else {
      setCities([]);
    }
  }, [selectedState]);

  useEffect(() => {
    if (selectedCity) {
      setLoadingPincode(true);
      fetch(`https://api.postalpincode.in/postoffice/${selectedCity}`)
        .then((res) => res.json())
        .then((data) => {
          if (data && data[0]?.Status === "Success" && data[0]?.PostOffice?.length > 0) {
            const exactMatch = data[0].PostOffice.find(
              (po: any) => po.State === selectedState || po.Circle === selectedState
            );
            if (exactMatch) {
              setValue(zipCodeField, exactMatch.Pincode);
            } else {
              setValue(zipCodeField, data[0].PostOffice[0].Pincode);
            }
          }
        })
        .catch((err) => {
          console.error("Failed to fetch pincode", err);
        })
        .finally(() => {
          setLoadingPincode(false);
        });
    }
  }, [selectedCity, selectedState, setValue, zipCodeField]);

  const handleStateChange = (e: any) => {
    setValue(stateField, e.target.value);
    setValue(cityField, ""); // Reset city when state changes
  };

  const FormFieldLabel = ({ label }: { label: string }) => (
    <Typography
      sx={
        variant === "mobileInfo" || variant === "userInfo"
          ? { textAlign: "start", mb: 0.5, fontWeight: variant === "userInfo" ? 600 : 500, color: variant === "userInfo" ? theme.palette.grey[800] : theme.palette.common.black }
          : {
              color: theme.palette.common.black,
              fontSize: isMobile ? 12 : 13,
              fontWeight: 500,
              mb: 0.5,
            }
      }
    >
      {label}
    </Typography>
  );

  const getVariantStyles = () => {
    if (variant === "grey") {
      return {
        "& .MuiOutlinedInput-root": {
          backgroundColor: theme.palette.grey[200],
          borderRadius: 1.5,
          "& fieldset": { border: "none" },
        },
      };
    }
    if (variant === "mobileInfo") {
      return {
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 1,
        backgroundColor: theme.palette.grey[100],
        width: "100%",
        "& .MuiOutlinedInput-root": {
          "& fieldset": { border: "none" },
          "&:hover fieldset": { border: "none" },
          "&.Mui-focused fieldset": { border: "none" },
          backgroundColor: "transparent",
          boxShadow: "none",
        },
        "& .MuiInputBase-input": {
          padding: "12px 14px",
        },
      };
    }
    if (variant === "userInfo") {
      return {
        width: "100%",
        "& .MuiOutlinedInput-root": {
          height: 52,
          borderRadius: 2,
          backgroundColor: theme.palette.grey[100],
          "& fieldset": {
            border: "none",
          },
          "&:hover": {
            backgroundColor: theme.palette.grey[200],
          },
          "&.Mui-focused": {
            backgroundColor: "#fff",
            border: `1px solid ${theme.palette.primary.main}`,
          },
        },
      };
    }
    return undefined;
  };

  const inputStyles: any = getVariantStyles();
  const showLabels = variant === "grey" || variant === "mobileInfo" || variant === "userInfo";

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Stack
        direction={layout ? layout : isMobile ? "column" : "row"}
        spacing={2}
      >
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          {showLabels && <FormFieldLabel label="State" />}
          <RHFSelect
            name={stateField}
            label=""
            placeholder="Select State"
            onChange={handleStateChange}
            sx={inputStyles}
          >
            {states.map((s: any) => (
              <MenuItem key={s.isoCode} value={s.name}>
                {s.name}
              </MenuItem>
            ))}
          </RHFSelect>
        </Stack>

        <Stack sx={{ flex: 1, minWidth: 0 }}>
          {showLabels && <FormFieldLabel label="City" />}
          <Tooltip
            title={!selectedState ? "Please select a state first" : ""}
            placement="top"
          >
            <Box sx={{ width: "100%" }}>
              <RHFSelect
                name={cityField}
                label=""
                placeholder="Select City"
                disabled={!selectedState || cities.length === 0}
                sx={inputStyles}
              >
                {cities.map((c: any) => (
                  <MenuItem key={c.name} value={c.name}>
                    {c.name}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Box>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack sx={{ flex: 1, minWidth: 0 }}>
        {showLabels && <FormFieldLabel label="Zip Code" />}
        <RHFTextField
          name={zipCodeField}
          placeholder="Zip Code"
          fullWidth
          rules={{
            required: "Zip Code is required",
            pattern: {
              value: /^[1-9][0-9]{5}$/,
              message: "Enter valid 6 digit zip code",
            },
          }}
          InputProps={{
            endAdornment: loadingPincode ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
          sx={inputStyles}
        />
      </Stack>
    </Stack>
  );
};

export { LocationSelector };
